import { useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Home, LogOut, MessageCircle, Plane, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDemo } from "@/store/DemoStore";
import { CURRENT_CLIENT_ID } from "@/data/constants";
import { Logo } from "@/components/common";
import FeedbackButton from "@/components/FeedbackButton";
import WelcomeTour from "@/components/WelcomeTour";
import NotificationBell from "@/components/NotificationBell";
import InstallPrompt from "@/components/InstallPrompt";
import { ensurePushSubscribed } from "@/lib/push";

const NAV = [
  { to: "/mytrip", label: "Home", full: "Dashboard", icon: Home, end: true, id: "home" },
  { to: "/mytrip/trips", label: "My Trips", full: "My Trips", icon: Plane, id: "trips" },
  { to: "/mytrip/messages", label: "Messages", full: "My Messages", icon: MessageCircle, id: "chat" },
  { to: "/mytrip/profile", label: "Profile", full: "My Profile", icon: User, id: "profile" },
];

const Dot = ({ n, className }) => (n > 0 ? <span data-testid="client-unread-badge" className={cn("grid h-4 min-w-4 place-items-center rounded-full bg-sky-500 px-1 text-[9px] font-bold text-white", className)}>{n}</span> : null);

export default function ClientLayout() {
  const { convFor, logout, me, client, settings, markWelcomeSeen } = useDemo();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/portal"); };
  const unread = convFor(CURRENT_CLIENT_ID)?.unreadClient || 0;
  const { pathname } = useLocation();
  const main = useRef(null);
  const isChat = pathname.startsWith("/mytrip/messages");
  const showWelcome = me?.kind === "client" && me.welcomeSeen === false;
  const firstName = client(CURRENT_CLIENT_ID)?.name?.split(" ")[0];
  const advisorFirst = settings.advisorName?.split(" ")[0];
  const pushEnabled = !!client(CURRENT_CLIENT_ID)?.prefs?.pushEnabled;

  useEffect(() => { if (main.current) main.current.scrollTop = 0; }, [pathname]);
  useEffect(() => { if (pushEnabled) ensurePushSubscribed(); }, [pushEnabled]);

  return (
    <div className="app-shell flex flex-col bg-[#F5F7FA]" data-testid="client-portal">
      <Link to="/" data-testid="back-to-website-banner" className="hidden h-9 shrink-0 items-center justify-center gap-1.5 bg-navy-900 sm:flex text-xs font-medium text-white/90 transition-colors hover:bg-navy-800">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Topsail Travel website
      </Link>
      <header className="hidden h-16 shrink-0 items-center border-b border-slate-200 bg-white md:flex">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-6">
          <Logo brand="mytrip" />
          <nav className="flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.id} to={n.to} end={n.end} data-testid={`client-topnav-${n.id}`} className={({ isActive }) => cn("flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors", isActive ? "bg-navy-900 text-white" : "text-slate-500 hover:text-navy-900")}>
                {n.full}{n.id === "chat" && <Dot n={unread} />}
              </NavLink>
            ))}
            <button onClick={onLogout} data-testid="client-exit-demo" className="ml-2 grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-navy-900" aria-label="Sign out"><LogOut className="h-4 w-4" /></button>
          </nav>
        </div>
      </header>
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 md:hidden">
        <Logo brand="mytrip" size="h-5" />
        <div className="flex items-center gap-0.5">
          <FeedbackButton variant="icon" />
          <NotificationBell enabled={pushEnabled} />
        </div>
      </header>
      <main ref={main} className={cn("min-h-0 flex-1", isChat ? "overflow-hidden" : "scroll-area")}>
        <div className={cn("mx-auto w-full max-w-5xl", isChat ? "h-full md:px-6 md:py-6" : "fade-in px-4 pb-10 pt-5 sm:px-6 md:pt-8")} key={pathname}>
          <Outlet />
        </div>
      </main>
      <nav className="hide-on-kb pb-safe grid shrink-0 grid-cols-4 border-t border-slate-200 bg-white/95 px-2 pt-1.5 backdrop-blur md:hidden" data-testid="client-bottom-nav">
        {NAV.map((n) => (
          <NavLink key={n.id} to={n.to} end={n.end} data-testid={`client-nav-${n.id}`} className={({ isActive }) => cn("relative flex flex-col items-center gap-1 rounded-lg py-1.5 text-[11px] font-medium", isActive ? "text-navy-900" : "text-slate-400")}>
            {({ isActive }) => (
              <>
                <span className={cn("grid h-7 w-12 place-items-center rounded-full transition-colors", isActive && "bg-navy-50")}><n.icon className="h-5 w-5" strokeWidth={isActive ? 2.1 : 1.75} /></span>
                {n.label}
                {n.id === "chat" && <Dot n={unread} className="absolute right-[22%] top-0.5" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <FeedbackButton className="hidden bottom-6 right-6 md:grid" />
      <InstallPrompt brand="mytrip" blocked={showWelcome} />
      {showWelcome && <WelcomeTour firstName={firstName} advisorFirst={advisorFirst} onClose={markWelcomeSeen} />}
    </div>
  );
}
